import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  CellText,
  ChevronRight,
  DatePicker,
  Drawer,
  EmptyState,
  FilterLines,
  PageHeader,
  Pagination,
  RefreshCw01,
  SearchLg,
  Select,
  Table,
  Tabs,
  TextInput,
  Tooltip,
  copyText,
} from "@opendate-app/setlist";
import { ArrowDown, ArrowRight, ArrowUp, Calendar } from "@untitled-ui/icons-react";
import client from "../../shared/api/client";
import { attributionChannelChartColor, attributionChannelColor } from "../shared/utils/attributionChannelColors";
import TableSettings from "../shared/components/TableSettings";
import { PROFILE_ACTIVITY_COLUMNS, getDefaultHiddenColumns, getDefaultVisibleColumns } from "./profileActivityColumnsConfig";
import "./profileActivity.css";

const PERIOD_LABELS = { "24h": "Last 24 hours", "7d": "Last 7 days", "30d": "Last 30 days", "90d": "Last 90 days", custom: "Custom range" };
const PERIOD_BUTTONS = { "24h": "24 hours", "7d": "7 days", "30d": "30 days", "90d": "90 days" };
const CUSTOM = "custom";
const CHANGE_STYLES = {
  up: { icon: ArrowUp, color: "var(--sl-text-success)" },
  down: { icon: ArrowDown, color: "var(--sl-text-error)" },
  flat: { icon: null, color: "var(--sl-text-tertiary)" },
};
const TAB_INTROS = {
  all: "Every touch in this period, from fans we know and visitors we can’t tie to a fan yet.",
  identified: "Touches from fans we know — linked at checkout or from an email or SMS click.",
  anonymous: "Touches from visitors we can’t tie to a fan yet. They move to Identified once they check out or click a message.",
};
const PROFILE_HEADERS = { all: "Fan or visitor", identified: "Fan", anonymous: "Visitor ID" };
const SEARCH_PLACEHOLDERS = { all: "Search fans, visitor ID, pages, campaigns", identified: "Search fans, pages, campaigns", anonymous: "Search visitor ID or page" };
const ALL = "all";
const COLUMN_WIDTHS = { profile: 260, channel: 180, source: 220, landing: 240, pages: 180, surface: 140, occurred_at: 240, linked_via: 140, visitor_id: 160 };
const MAX_CUSTOM_DAYS = 366;
const DAY_MS = 86400000;
const CHECKED_TICK_MS = 15000;
const SEARCH_DEBOUNCE_MS = 300;
const CHART_HEIGHT = 180;
const PAGE_VIEW_COLOR = "var(--sl-bg-brand-solid)";
const JOURNEY_DOTS = { page: "var(--sl-fg-quaternary)", purchase: "var(--sl-fg-success)" };

const formatNumber = (value) => Number(value || 0).toLocaleString();

const CHANNEL_SORT_COLUMNS = [
  { key: "count", label: "Touches" },
  { key: "purchases", label: "Purchases", numeric: true },
  { key: "conversionPct", label: "Conv. rate", numeric: true },
  { key: "revenueCents", label: "Revenue", numeric: true },
];

const sortChannels = (rows, { key, dir }) =>
  [...rows].sort((a, b) => {
    if (a[key] === undefined || b[key] === undefined) return (a[key] === undefined) - (b[key] === undefined);
    return dir === "asc" ? a[key] - b[key] : b[key] - a[key];
  });

const formatMoney = (cents, currency) => {
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency, maximumFractionDigits: 0 }).format(cents / 100);
  } catch (e) {
    return (cents / 100).toFixed(0);
  }
};

const isoDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
const shortDate = (date) => date.toLocaleDateString(undefined, { month: "short", day: "numeric" });

const niceMax = (value) => {
  if (value <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(value));
  return Math.ceil(value / magnitude) * magnitude;
};

const ChannelBadge = ({ channel, label }) => (
  <Badge color={attributionChannelColor(channel)} dot>
    {label}
  </Badge>
);

ChannelBadge.propTypes = { channel: PropTypes.string.isRequired, label: PropTypes.string.isRequired };

const storageKey = (teamId) => `profileActivityColumns_${teamId}`;

const loadSavedColumns = (teamId) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(teamId)));
    if (!saved) return null;
    const known = new Map(PROFILE_ACTIVITY_COLUMNS.map((c) => [c.id, c]));
    const placed = new Set([...saved.visible, ...saved.hidden].map((c) => c.id));
    const unplaced = PROFILE_ACTIVITY_COLUMNS.filter((c) => !placed.has(c.id) && !c.alwaysVisible);
    const visible = [...saved.visible.map((c) => known.get(c.id)).filter(Boolean), ...unplaced.filter((c) => c.defaultVisible)];
    const hidden = [...saved.hidden.map((c) => known.get(c.id)).filter(Boolean), ...unplaced.filter((c) => !c.defaultVisible)];
    return { visible, hidden };
  } catch (e) {
    return null;
  }
};

const KpiCard = ({ metric, priorLabel, currency }) => {
  const change = metric.change && CHANGE_STYLES[metric.change.direction];
  const Icon = change?.icon;
  return (
    <Card>
      <div className="d-flex align-items-center justify-content-between pa-gap-8">
        <span className="pa-kpi-label">{metric.label}</span>
        {metric.isNew && <Badge color="brand" size="sm">New</Badge>}
      </div>
      <div className="pa-kpi-value mt-2">{`${formatNumber(metric.value)}${metric.unit || ""}`}</div>
      {change && (
        <div className="d-flex align-items-center pa-kpi-change mt-2">
          {Icon && <Icon width={16} height={16} color={change.color} aria-hidden="true" />}
          <span style={{ color: change.color }}>{metric.change.value}</span>
          <span className="pa-muted font-weight-normal">{priorLabel}</span>
        </div>
      )}
      <div className="pa-muted mt-3">
        {metric.revenueCents !== undefined ? `${formatMoney(metric.revenueCents, currency)} ${metric.note}` : metric.note}
      </div>
    </Card>
  );
};

KpiCard.propTypes = { metric: PropTypes.object.isRequired, priorLabel: PropTypes.string.isRequired, currency: PropTypes.string };

const ActivityBreakdown = ({ bucket, total, pageViews, channels, channelLabels }) => {
  const rows = pageViews
    ? [["pageViews", "Page views", PAGE_VIEW_COLOR, bucket.pageViews]]
    : channels
        .filter((channel) => bucket.touches[channel])
        .sort((a, b) => bucket.touches[b] - bucket.touches[a])
        .map((channel) => [channel, channelLabels[channel] || channel, attributionChannelChartColor(channel), bucket.touches[channel]]);

  return (
    <span className="pa-tip">
      {rows.map(([key, label, color, count]) => (
        <span key={key} className="pa-tip-row">
          <span className="pa-swatch" style={{ background: color }} />
          <span className="pa-tip-label">{label}</span>
          <span className="pa-numeric">{formatNumber(count)}</span>
        </span>
      ))}
      {!pageViews && (
        <span className="pa-tip-row pa-tip-total">
          <span className="pa-tip-label">{rows.length ? "Total touches" : "No touches"}</span>
          <span className="pa-numeric">{formatNumber(total)}</span>
        </span>
      )}
    </span>
  );
};

ActivityBreakdown.propTypes = {
  bucket: PropTypes.shape({ touches: PropTypes.object, pageViews: PropTypes.number }).isRequired,
  total: PropTypes.number.isRequired,
  pageViews: PropTypes.bool.isRequired,
  channels: PropTypes.arrayOf(PropTypes.string).isRequired,
  channelLabels: PropTypes.object.isRequired,
};

const ActivityChart = ({ buckets: allBuckets, metric, channel, channelLabels }) => {
  const pageViews = metric === "pageViews";
  const buckets = channel === ALL ? allBuckets : allBuckets.map((bucket) => ({ ...bucket, touches: bucket.touches[channel] ? { [channel]: bucket.touches[channel] } : {} }));
  const totals = buckets.map((bucket) => (pageViews ? bucket.pageViews : Object.values(bucket.touches).reduce((sum, n) => sum + n, 0)));
  const yMax = niceMax(Math.max(...totals, 0) * 1.1);
  const channels = channel === ALL ? [...new Set(buckets.flatMap((bucket) => Object.keys(bucket.touches)))] : [channel];
  const labelEvery = Math.ceil(buckets.length / 8);
  const gap = buckets.length > 20 ? 3 : 8;

  return (
    <>
      <div className="d-flex pa-gap-12">
        <div className="pa-chart-axis pa-numeric" style={{ height: CHART_HEIGHT }}>
          <span>{formatNumber(yMax)}</span>
          <span>{formatNumber(yMax / 2)}</span>
          <span>0</span>
        </div>
        <div className="pa-chart-main">
          <div
            role="group"
            aria-label={pageViews ? "Page views over time" : "Touches by channel over time"}
            className="pa-chart-plot"
            style={{ height: CHART_HEIGHT, gap, backgroundSize: `100% ${CHART_HEIGHT / 2}px` }}
          >
            {buckets.map((bucket, i) => (
              <Tooltip key={`${bucket.label}-${i}`} title={bucket.label} supportingText={<ActivityBreakdown bucket={bucket} total={totals[i]} pageViews={pageViews} channels={channels} channelLabels={channelLabels} />}>
                <div tabIndex={0} className="pa-chart-col">
                  <div className="pa-chart-bar" style={{ height: `${(totals[i] / yMax) * 100}%` }}>
                    {pageViews ? (
                      <div style={{ height: "100%", background: PAGE_VIEW_COLOR }} />
                    ) : (
                      channels
                        .filter((channel) => bucket.touches[channel])
                        .map((channel) => (
                          <div key={channel} style={{ height: `${(bucket.touches[channel] / totals[i]) * 100}%`, background: attributionChannelChartColor(channel) }} />
                        ))
                    )}
                  </div>
                </div>
              </Tooltip>
            ))}
          </div>
          <div className="d-flex mt-2" style={{ gap }}>
            {buckets.map((bucket, i) => (
              <div key={`${bucket.label}-${i}`} className="pa-chart-label">
                {i % labelEvery === 0 ? bucket.label : ""}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="d-flex flex-wrap pa-legend mt-3">
        {(pageViews ? [["pageViews", "Page views", PAGE_VIEW_COLOR]] : channels.map((channel) => [channel, channelLabels[channel] || channel, attributionChannelChartColor(channel)])).map(([key, label, color]) => (
          <span key={key} className="d-inline-flex align-items-center pa-legend-item">
            <span className="pa-swatch" style={{ background: color }} />
            {label}
          </span>
        ))}
      </div>
    </>
  );
};

ActivityChart.propTypes = {
  buckets: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, touches: PropTypes.object, pageViews: PropTypes.number })).isRequired,
  metric: PropTypes.string.isRequired,
  channel: PropTypes.string.isRequired,
  channelLabels: PropTypes.object.isRequired,
};

const DETAIL_LABELS = { landing_url: "Landing URL", referrer: "Referrer" };

const plural = (count, word) => `${formatNumber(count)} ${word}${count === 1 ? "" : "s"}`;

const journeySummary = (detail) => {
  const pages = detail.journey.filter((step) => step.kind === "page").length;
  return [pages ? plural(pages, "page") : "No pages viewed yet", detail.purchasedAt && "bought tickets"].filter(Boolean).join(" · ");
};

const touchDetails = (detail, identified, lookbackDays) => [
  ["Channel", detail.channelLabel],
  ...detail.details
    .filter((d) => d.key !== "channel")
    .map((d) => {
      if (d.key === "surface") return ["Surface", detail.surfaceLabel];
      if (d.key === "landing_url") return [DETAIL_LABELS[d.key], d.value.replace(/^https?:\/\/(www\.)?/i, "")];
      return [DETAIL_LABELS[d.key] || d.key, d.value];
    }),
  ...(identified
    ? [
        ["Linked via", detail.linkedVia || "—"],
        ["Conversion", detail.purchasedAt ? `Purchased ${detail.purchasedAt.replace(/^Today/, "today").replace(/^Yesterday/, "yesterday")}` : `None within ${lookbackDays}-day lookback yet`],
      ]
    : [
        ["Visitor", detail.visitorUuid || "—"],
        ["Identity", "Not linked to a fan yet — links on checkout, email or SMS click"],
      ]),
];

const Journey = ({ steps }) => (
  <div className="d-flex flex-column">
    {steps.map((step, i) => {
      const dot = step.kind === "touch" ? attributionChannelChartColor(step.channel) : JOURNEY_DOTS[step.kind];
      return (
        <div key={`${step.kind}-${i}`} className="pa-journey-step">
          <span className="pa-journey-time">{step.time}</span>
          <span className="d-flex flex-column align-items-center">
            <span className="pa-journey-dot" style={{ background: dot }} />
            {i < steps.length - 1 && <span className="pa-journey-line" />}
          </span>
          <div className="pa-journey-body">
            <div className={`pa-journey-title${step.kind === "page" ? " pa-journey-title--page" : ""}`}>{step.title}</div>
            <div className="pa-muted pa-wrap">{step.sub}</div>
          </div>
        </div>
      );
    })}
  </div>
);

Journey.propTypes = { steps: PropTypes.arrayOf(PropTypes.object).isRequired };

const AttributionProfileActivityApp = ({ dataUrl, attributionReportUrl, teamId, title, currency, trackingStartedAt, pageViewsStartedAt, periods, defaultPeriod, lookbackDays }) => {
  const [tab, setTab] = useState("all");
  const [period, setPeriod] = useState(defaultPeriod);
  const [customRange, setCustomRange] = useState(null);
  const [channel, setChannel] = useState(ALL);
  const [channelSort, setChannelSort] = useState({ key: "count", dir: "desc" });
  const [surface, setSurface] = useState(ALL);
  const [event, setEvent] = useState(ALL);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [chartMetric, setChartMetric] = useState("touches");
  const [feed, setFeed] = useState(null);
  const [summary, setSummary] = useState(null);
  const [summaryError, setSummaryError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkedAt, setCheckedAt] = useState(Date.now());
  const [now, setNow] = useState(Date.now());
  const [refreshKey, setRefreshKey] = useState(0);
  const [detail, setDetail] = useState(null);
  const [showJson, setShowJson] = useState(false);
  const [tableSettingsOpen, setTableSettingsOpen] = useState(false);
  const saved = useMemo(() => loadSavedColumns(teamId), [teamId]);
  const [visibleColumns, setVisibleColumns] = useState(saved?.visible || getDefaultVisibleColumns());
  const [hiddenColumns, setHiddenColumns] = useState(saved?.hidden || getDefaultHiddenColumns());
  const loadIdRef = useRef(0);
  const summaryIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), CHECKED_TICK_MS);
    return () => clearInterval(timer);
  }, []);

  const customSpanDays = customRange?.start && customRange?.end ? Math.round((customRange.end - customRange.start) / DAY_MS) + 1 : 0;
  const customError = customSpanDays > MAX_CUSTOM_DAYS ? `Pick a range of up to ${MAX_CUSTOM_DAYS} days.` : null;
  const customReady = period !== CUSTOM || (customSpanDays > 0 && !customError);
  const rangeParams = period === CUSTOM && customReady ? { from: isoDate(customRange.start), to: isoDate(customRange.end) } : {};

  useEffect(() => {
    if (!customReady) return;
    const summaryId = (summaryIdRef.current += 1);
    setSummaryError(null);
    client
      .get(dataUrl, { params: { tab, period, ...rangeParams, summary: "1" } })
      .then(({ data }) => {
        if (summaryIdRef.current === summaryId) setSummary(data.summary);
      })
      .catch(() => {
        if (summaryIdRef.current !== summaryId) return;
        setSummary(null);
        setSummaryError("Could not load the summary. Refresh to try again.");
      });
  }, [dataUrl, tab, period, rangeParams.from, rangeParams.to, refreshKey]);

  useEffect(() => {
    if (!customReady) return;
    const loadId = (loadIdRef.current += 1);
    setLoading(true);
    setError(null);
    client
      .get(dataUrl, {
        params: {
          tab,
          period,
          ...rangeParams,
          channel: channel === ALL ? undefined : channel,
          surface: surface === ALL ? undefined : surface,
          event: event === ALL ? undefined : event,
          q: query || undefined,
          page,
        },
      })
      .then(({ data }) => {
        if (loadIdRef.current !== loadId) return;
        setFeed(data);
        setCheckedAt(Date.now());
        setNow(Date.now());
      })
      .catch(() => {
        if (loadIdRef.current === loadId) setError("Could not load profile activity.");
      })
      .finally(() => {
        if (loadIdRef.current === loadId) setLoading(false);
      });
  }, [dataUrl, tab, period, rangeParams.from, rangeParams.to, channel, surface, event, query, page, refreshKey]);

  const resetFilters = () => {
    setChannel(ALL);
    setSurface(ALL);
    setEvent(ALL);
    setSearch("");
    setQuery("");
    setPage(1);
  };

  const changeTab = (value) => {
    if (value !== tab) setSummary(null);
    setTab(value);
    resetFilters();
  };

  const updateFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const changePeriod = (value) => {
    if (value !== period) setSummary(null);
    setPeriod(value);
    setEvent(ALL);
    setPage(1);
  };

  const openDetail = (row) => {
    setShowJson(false);
    setDetail(row);
  };

  const handleColumnsChange = (visible, hidden) => {
    setVisibleColumns(visible);
    setHiddenColumns(hidden);
    try {
      localStorage.setItem(storageKey(teamId), JSON.stringify({ visible, hidden }));
    } catch (e) {
      return;
    }
  };

  const resetColumns = () => handleColumnsChange(getDefaultVisibleColumns(), getDefaultHiddenColumns());

  const tabCounts = feed?.tabCounts || {};
  const minutesSinceCheck = Math.floor((now - checkedAt) / 60000);
  const fellBack = period === CUSTOM && customReady && summary?.period && summary.period !== CUSTOM;
  const custom = period === CUSTOM && customReady && !fellBack;
  const shownPeriod = fellBack ? summary.period : period;
  const rangeLabel = custom ? `${shortDate(customRange.start)} – ${shortDate(customRange.end)}` : PERIOD_LABELS[shownPeriod] || shownPeriod;
  const priorText = custom ? "the prior period" : `the prior ${PERIOD_BUTTONS[shownPeriod] || shownPeriod}`;
  const priorLabel = custom ? "vs prior period" : `vs prior ${PERIOD_BUTTONS[shownPeriod] || shownPeriod}`;
  const channelLabels = { ...(summary?.channelLabels || {}), ...Object.fromEntries((summary?.channels || []).map((row) => [row.channel, row.label])) };

  const columns = useMemo(
    () => [
      ...visibleColumns.map((col) => {
        switch (col.id) {
          case "profile":
            return {
              key: "profile",
              header: PROFILE_HEADERS[tab],
              primary: true,
              render: (row) => (
                <span className="d-flex align-items-center pa-gap-8">
                  {row.fan ? (
                    <CellText primary={<a href={row.fan.url}>{row.fan.name}</a>} supporting={row.fan.email} />
                  ) : (
                    <span className="pa-mono" title={row.visitorUuid}>
                      {row.visitorShort || "—"}
                    </span>
                  )}
                  {row.purchasedAt && <Badge color="success">Purchased</Badge>}
                </span>
              ),
            };
          case "channel":
            return { key: "channel", header: "Channel", render: (row) => <ChannelBadge channel={row.channel} label={row.channelLabel} /> };
          case "source":
            return {
              key: "source",
              header: "Source · campaign",
              render: (row) => <CellText primary={row.source || "—"} supporting={row.campaign || "No campaign"} />,
            };
          case "landing":
            return { key: "landing", header: "Landing page", render: (row) => <span title={row.landingPath}>{row.landingPath || "—"}</span> };
          case "pages":
            return {
              key: "pages",
              header: "Pages viewed",
              render: (row) =>
                row.pages
                  ? `${formatNumber(row.pages.count)} ${row.pages.count === 1 ? "page" : "pages"} · ${row.pages.sessions} ${row.pages.sessions === 1 ? "session" : "sessions"}`
                  : "—",
            };
          case "surface":
            return {
              key: "surface",
              header: "Surface",
              render: (row) => (
                <Tooltip title={row.surfaceDescription}>
                  <span tabIndex={0} className="pa-help">
                    {row.surfaceLabel}
                  </span>
                </Tooltip>
              ),
            };
          case "occurred_at":
            return { key: "occurred_at", header: "Occurred at", render: (row) => row.occurredAt };
          case "linked_via":
            return { key: "linked_via", header: "Linked via", render: (row) => row.linkedVia || "—" };
          case "visitor_id":
            return { key: "visitor_id", header: "Visitor ID", render: (row) => <span className="pa-mono">{row.visitorShort || "—"}</span> };
          default:
            return { key: col.id, header: col.label, render: () => "—" };
        }
      }),
      {
        key: "expand",
        header: "",
        width: 64,
        render: (row) => <Button variant="tertiary" size="sm" icon={ChevronRight} aria-label="Show journey and touch details" onClick={() => openDetail(row)} />,
      },
    ].map((column) => ({ width: COLUMN_WIDTHS[column.key], ...column })),
    [visibleColumns, tab]
  );

  const eventColumns = [
    { key: "title", header: "Event", primary: true, render: (row) => <CellText primary={row.title} supporting={row.date} /> },
    { key: "views", header: "Views", align: "right", render: (row) => formatNumber(row.views) },
    { key: "visitors", header: "Visitors", align: "right", render: (row) => formatNumber(row.visitors) },
    {
      key: "bought",
      header: "Bought tickets",
      align: "right",
      render: (row) => <Badge color={row.boughtPct >= 5 ? "success" : row.boughtPct < 2 ? "error" : "gray"}>{`${row.boughtPct}%`}</Badge>,
    },
  ];

  const maxChannelCount = Math.max(...(summary?.channels || []).map((row) => row.count), 1);
  const sortedChannels = sortChannels(summary?.channels || [], channelSort);
  const toggleChannelSort = (key) => setChannelSort((prev) => ({ key, dir: prev.key === key && prev.dir === "desc" ? "asc" : "desc" }));
  const channelOptions = [{ value: ALL, label: "All channels" }, ...(feed?.channelOptions || [])];
  const surfaceOptions = [{ value: ALL, label: "All surfaces" }, ...(feed?.surfaceOptions || [])];
  const eventOptions = [{ value: ALL, label: "All events" }, ...(feed?.eventOptions || [])];
  const detailJson = detail && JSON.stringify(Object.fromEntries(detail.details.map((d) => [d.key, d.value])), null, 2);

  return (
    <div className="od-themed profile-activity">
      <PageHeader
        divider={false}
        title={title}
        supportingText={[trackingStartedAt && `Tracking since ${trackingStartedAt}`, pageViewsStartedAt && `Page views since ${pageViewsStartedAt}`].filter(Boolean).join(" · ")}
        actions={
          <div className="d-flex align-items-start pa-gap-12">
            {period === CUSTOM && (
              <DatePicker
                id="profile-activity-range"
                mode="range"
                size="sm"
                value={customRange}
                onChange={(range) => {
                  setSummary(null);
                  setCustomRange(range);
                  setPage(1);
                }}
                max={new Date()}
                placeholder="Pick dates"
                error={customError}
                aria-label="Custom date range"
              />
            )}
            <Tabs
              variant="button-border"
              collapseBelow={0}
              value={period}
              onChange={changePeriod}
              items={[...periods.map((value) => ({ value, label: PERIOD_BUTTONS[value] || value })), { value: CUSTOM, label: "Custom", icon: Calendar }]}
            />
          </div>
        }
      />

      <div className="d-flex flex-wrap align-items-center justify-content-between mt-4 pa-gap-16">
        <Tabs
          variant="button-border"
          collapseBelow={0}
          value={tab}
          onChange={changeTab}
          items={[
            { value: "all", label: "All", count: formatNumber(tabCounts.all) },
            { value: "identified", label: "Identified", count: formatNumber(tabCounts.identified) },
            { value: "anonymous", label: "Anonymous", count: formatNumber(tabCounts.anonymous) },
          ]}
        />
        {customReady && (
          <span className="pa-muted">
            Showing <strong className="pa-strong">{rangeLabel}</strong> compared with {priorText}
          </span>
        )}
      </div>
      <p className="mt-3 pa-muted">{TAB_INTROS[tab]}</p>

      {fellBack && (
        <div className="mt-3">
          <Alert color="warning" title="That date range can’t be shown, so this is the last 7 days instead." />
        </div>
      )}

      {summaryError && (
        <div className="mt-3">
          <Alert color="error" role="alert" title={summaryError} />
        </div>
      )}

      {customReady && summary && (
        <>
          <div className="mt-3 pa-kpis">
            {summary.metrics.map((metric) => (
              <KpiCard key={metric.label} metric={metric} priorLabel={priorLabel} currency={currency} />
            ))}
          </div>

          <div className="mt-3">
            <Card
              header={
                <CardHeader
                  title="Activity over time"
                  supportingText={`${chartMetric === "pageViews" ? "Page views" : `Touches${channel === ALL ? " by channel" : ` from ${channelLabels[channel] || channel}`}`} · ${rangeLabel}`}
                  actions={
                    <Tabs
                      variant="button-minimal"
                      collapseBelow={0}
                      value={chartMetric}
                      onChange={setChartMetric}
                      items={[
                        { value: "touches", label: "Touches by channel" },
                        { value: "pageViews", label: "Page views" },
                      ]}
                    />
                  }
                />
              }
            >
              <ActivityChart buckets={summary.activity} metric={chartMetric} channel={channel} channelLabels={channelLabels} />
            </Card>
          </div>

          <div className="mt-3 pa-cards">
            <Card
              header={
                <CardHeader
                  title="Channels"
                  supportingText={`${rangeLabel} · click a channel to filter the chart and feed`}
                  actions={
                    <Button as="a" variant="secondary-color" href={attributionReportUrl} trailingIcon={ArrowRight}>
                      Attribution report
                    </Button>
                  }
                />
              }
            >
              <div className="pa-channel-scroll">
                <div className="pa-channel-grid pa-channel-head">
                  <span>Channel</span>
                  {CHANNEL_SORT_COLUMNS.map(({ key, label, numeric }) => {
                    const active = channelSort.key === key;
                    const ascending = channelSort.dir === "asc";
                    return (
                      <span key={key} className={numeric ? "pa-numeric" : undefined}>
                        <Button
                          variant="link-gray"
                          size="sm"
                          className="pa-sort"
                          onClick={() => toggleChannelSort(key)}
                          trailingIcon={active ? (ascending ? ArrowUp : ArrowDown) : undefined}
                          aria-label={active ? `${label}, sorted ${ascending ? "ascending" : "descending"}` : `Sort by ${label}`}
                        >
                          {label}
                        </Button>
                      </span>
                    );
                  })}
                </div>
                {sortedChannels.length === 0 ? (
                  <span className="pa-muted">No touches in this period.</span>
                ) : (
                  sortedChannels.map((row) => (
                    <button
                      key={row.channel}
                      type="button"
                      onClick={() => updateFilter(setChannel)(channel === row.channel ? ALL : row.channel)}
                      aria-pressed={channel === row.channel}
                      className="pa-channel-grid pa-channel-row"
                    >
                      <span>
                        <ChannelBadge channel={row.channel} label={row.label} />
                      </span>
                      <span className="d-flex align-items-center pa-gap-8">
                        <span className="pa-bar-track">
                          <span className="pa-bar-fill" style={{ width: `${Math.round((row.count / maxChannelCount) * 100)}%`, background: attributionChannelChartColor(row.channel) }} />
                        </span>
                        <span className="pa-numeric pa-count">
                          {formatNumber(row.count)}
                        </span>
                      </span>
                      <span className="pa-numeric">{row.purchases === undefined ? "—" : formatNumber(row.purchases)}</span>
                      <span className="pa-numeric">{row.conversionPct === undefined ? "—" : `${row.conversionPct}%`}</span>
                      <span className="pa-numeric">{row.revenueCents === undefined ? "—" : formatMoney(row.revenueCents, currency)}</span>
                    </button>
                  ))
                )}
              </div>
            </Card>
            <Card padding="none" header={<CardHeader title="Most viewed events" supportingText={`From page views · ${rangeLabel}`} />}>
              <Table
                columns={eventColumns}
                data={summary.topEvents}
                getRowKey={(row) => row.id}
                empty={<EmptyState title="No event page views yet" description="Event pages show up here once visitors view them." />}
              />
            </Card>
          </div>

          {summary.audiences.length > 0 && (
            <div className="mt-3">
              <Card header={<CardHeader title="Ready to convert" supportingText={`High-intent people who haven’t bought yet · ${rangeLabel}`} />}>
                <div className="pa-audiences">
                  {summary.audiences.map((audience) => (
                    <div key={audience.key}>
                      <div className="pa-audience-count">{formatNumber(audience.count)}</div>
                      <div className="pa-audience-label mt-1">{audience.label}</div>
                      <div className="pa-muted">{audience.sub}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </>
      )}

      {customReady ? (
        <>
          <div className="d-flex align-items-center justify-content-end mt-4 pa-gap-8">
            <span className="pa-muted">{minutesSinceCheck < 1 ? "Checked just now" : `Checked ${minutesSinceCheck} min ago`}</span>
            <Button variant="secondary" icon={RefreshCw01} aria-label="Refresh" onClick={() => setRefreshKey((key) => key + 1)} />
          </div>

          <div className="d-flex flex-wrap align-items-end mt-3 mb-3 pa-gap-12">
            <Select id="profile-activity-channel" options={channelOptions} value={channel} onChange={updateFilter(setChannel)} width={190} />
            <Select id="profile-activity-surface" options={surfaceOptions} value={surface} onChange={updateFilter(setSurface)} width={170} />
            <Select id="profile-activity-event" options={eventOptions} value={event} onChange={updateFilter(setEvent)} width={200} />
            <div className="pa-search">
              <TextInput
                leadingIcon={SearchLg}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder={SEARCH_PLACEHOLDERS[tab]}
              />
            </div>
            <Button variant="link-gray" onClick={resetFilters}>
              Clear filters
            </Button>
            <div className="ml-auto position-relative">
              <Button variant="secondary" icon={FilterLines} onClick={() => setTableSettingsOpen((open) => !open)}>
                Table Settings
              </Button>
              <TableSettings
                isOpen={tableSettingsOpen}
                onClose={() => setTableSettingsOpen(false)}
                visibleFields={visibleColumns}
                hiddenFields={hiddenColumns}
                onFieldsChange={handleColumnsChange}
                onResetToDefault={resetColumns}
              />
            </div>
          </div>

          <Card padding="none">
            <Table
              columns={columns}
              data={feed?.rows || []}
              loading={loading}
              error={error}
              getRowKey={(row) => row.id}
              stickyFirstColumn
              stickyLastColumn
              empty={<EmptyState title="No activity matches these filters" description="Try a different channel, surface, event or period." />}
            />
            <div className="d-flex align-items-center pa-feed-footer">
              <span className="pa-muted text-nowrap">{formatNumber(feed?.totalCount)} touches</span>
              <div className="flex-grow-1">
                <Pagination variant="simple" currentPage={page} totalPages={feed?.totalPages || 1} onPageChange={setPage} />
              </div>
            </div>
          </Card>
        </>
      ) : (
        <div className="mt-4">
          <EmptyState title="Pick a date range" description="Choose a start and end date to see activity for a custom range." />
        </div>
      )}

      <Drawer open={!!detail} onClose={() => setDetail(null)} title={detail?.fan?.name || detail?.visitorShort || "Touch"} subtitle={detail?.occurredAt}>
        {detail && (
          <div className="pa-drawer">
            <Card>
              <div className="d-flex flex-wrap align-items-baseline pa-gap-title">
                <span className="pa-section-title">Journey</span>
                <span className="pa-muted">{journeySummary(detail)}</span>
              </div>
              <div className="mt-3">
                <Journey steps={detail.journey} />
              </div>
            </Card>

            <Card>
              <div className="pa-section-title">Touch details</div>
              <dl className="mt-3 pa-details">
                {touchDetails(detail, !!detail.fan, lookbackDays).map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
              <div className="d-flex flex-wrap mt-3 pa-gap-8">
                {detail.fan && (
                  <Button as="a" variant="secondary" size="sm" href={detail.fan.url}>
                    Open fan profile
                  </Button>
                )}
                {detail.visitorUuid && (
                  <Button variant="secondary" size="sm" onClick={() => copyText(detail.visitorUuid)}>
                    Copy visitor ID
                  </Button>
                )}
                <Button variant="tertiary" size="sm" onClick={() => setShowJson((open) => !open)}>
                  {showJson ? "Hide raw JSON" : "View raw JSON"}
                </Button>
              </div>
              {showJson && (
                <pre className="mt-3 pa-json">
                  {detailJson}
                </pre>
              )}
            </Card>
          </div>
        )}
      </Drawer>
    </div>
  );
};

AttributionProfileActivityApp.propTypes = {
  dataUrl: PropTypes.string.isRequired,
  attributionReportUrl: PropTypes.string.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  title: PropTypes.string.isRequired,
  currency: PropTypes.string,
  trackingStartedAt: PropTypes.string,
  pageViewsStartedAt: PropTypes.string,
  periods: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultPeriod: PropTypes.string.isRequired,
  lookbackDays: PropTypes.number.isRequired,
};

export default AttributionProfileActivityApp;
